import React, { useState, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { MapIcon, PhoneIcon, ClockIcon, AtSymbolIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ContactUsPayload } from '../../../types/user.types';
import { useUserService } from '../../../services/user.service';
import styles from './Contact.module.scss';
import SectionHeader from '../HomeModern/components/SectionHeader/SectionHeader';
import PromoCard from '../HomeModern/components/PromoCard/PromoCard';
import HighlightCard from '../HomeModern/components/HighlightCard/HighlightCard';
import Button from '../../Buttons/Button/Button';
import { ButtonStyleType, TextSize } from '../../../types/button.types';
import { NavigateToInternalUrl } from '../../../utils/api.utils';
import { Routes } from '../../../types/constants';
import { HomeHighlight, HomePromo } from '../../../types/home.types';
import { fetchPromos } from '../../../services/promo.service';
import { fetchHighlights } from '../../../services/highlight.service';
import { trackMarketingConversion } from '../../../services/marketing.service';
import { buildJoinUrl } from '../../../utils/join';

const ContactUsPage = () => {
    const userService = useUserService();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [promos, setPromos] = useState<HomePromo[]>([]);
    const [promoStatus, setPromoStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [highlights, setHighlights] = useState<HomeHighlight[]>([]);
    const [highlightStatus, setHighlightStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [highlightError, setHighlightError] = useState<string | null>(null);

    const [errors, setErrors] = useState({
        captcha: '',
        firstName: '',
        lastName: '',
        email: '',
        message: ''
    });

    const [contactInfo, setContactInfo] = useState({
        address: '',
        phone: '',
        email: '',
        hours: ''
    });

    useEffect(() => {
        const fetchSiteInfo = async () => {
            setIsLoading(true);
            try {
                const response = await userService.siteInfo();
                if (response) {
                    setContactInfo({
                        address: response.Address || 'N/A',
                        phone: response.ContactPhone || 'N/A',
                        email: response.ContactEmail || 'N/A',
                        hours: 'Monday - Friday, 9am - 5pm'
                    });
                }
            } catch (error) {
                console.error("Error fetching site info:", error);                
            } finally {
                setIsLoading(false);
            }
        };

        fetchSiteInfo();
        // useUserService returns a new facade each render; site information intentionally loads once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let isMounted = true;
        setPromoStatus('loading');

        fetchPromos()
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                setPromos(data);
                setPromoStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Promo fetch failed', { error });
                setPromos([]);
                setPromoStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        setHighlightStatus('loading');
        setHighlightError(null);

        fetchHighlights()
            .then((data) => {
                if (!isMounted) {
                    return;
                }
                setHighlights(data);
                setHighlightStatus('success');
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                console.error('Highlight fetch failed', { error });
                setHighlights([]);
                setHighlightStatus('error');
                setHighlightError('Unable to load highlights.');
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        let formErrors = { captcha: '', firstName: '', lastName: '', email: '', message: '' };

        if (!isVerified) {
            formErrors.captcha = 'Please complete the CAPTCHA.';
        }
        if (!firstName) {
            formErrors.firstName = 'First name is required.';
        }
        if (!lastName) {
            formErrors.lastName = 'Last name is required.';
        }
        if (!email) {
            formErrors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            formErrors.email = 'Please enter a valid email address.';
        }
        if (!message) {
            formErrors.message = 'Message is required.';
        }

        setErrors(formErrors);

        if (Object.values(formErrors).some(error => error)) {
            return;
        }

        const payload: ContactUsPayload = {
            contactUsRequest: {
                firstName,
                lastName,
                emailAddress: email,
                message
            }
        };
        
        try {
            setIsSubmitting(true);
            const response = await userService.contactUs(payload);

            if (response.message) {
                toast.success(response.message);
                trackMarketingConversion({
                    kind: 'lead',
                    metadata: { source: 'contact_page' }
                });
                setFirstName('');
                setLastName('');
                setEmail('');
                setMessage('');
                setIsVerified(false);
                setErrors({ captcha: '', firstName: '', lastName: '', email: '', message: '' });
            } else {
                toast.error('Form submission failed');
            }
        } catch (error) {
            toast.error('Error submitting form');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCaptchaChange = (value: string | null) => {
        setIsVerified(!!value);
        setErrors(prevErrors => ({ ...prevErrors, captcha: '' }));
    };

    const siteKey = process.env.REACT_APP_SITE_KEY;
    const isCaptchaReady = Boolean(siteKey);
    const shouldRenderPromoSection = promoStatus === 'success' && promos.length > 0;

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <span className={styles.heroTag}>Contact us</span>
                    <h1 className={styles.heroTitle}>Let’s connect your fight night crew.</h1>
                    <p className={styles.heroSubtitle}>
                        Reach out for support, partnerships, or scheduling help. Our team is ready to keep your
                        streams on track.
                    </p>
                    <div className={styles.heroActions}>
                        <Button
                            style={[styles.primaryButton, styles.heroButton].join(' ')}
                            styleType={ButtonStyleType.FILL}
                            textSize={TextSize.LARGE}
                            text="Subscribe now"
                            callback={() => NavigateToInternalUrl(buildJoinUrl({ source: 'contact' }))}
                        />
                        <Button
                            style={[styles.secondaryButton, styles.heroButton].join(' ')}
                            styleType={ButtonStyleType.OUTLINE}
                            textSize={TextSize.MEDIUM}
                            text="Browse content"
                            callback={() => NavigateToInternalUrl(Routes.VIDEOS)}
                        />
                    </div>
                </div>
                <div className={styles.heroCard}>
                    <p className={styles.heroCardTitle}>We’re here to help</p>
                    <p className={styles.heroCardCopy}>
                        Expect a reply within one business day. For immediate help, see the contact details below.
                    </p>
                    <ul className={styles.heroHighlights}>
                        <li>On-demand replays &amp; highlights support</li>
                        <li>Partner onboarding</li>
                        <li>Billing &amp; subscriptions</li>
                    </ul>
                </div>
            </section>

            <section className={styles.contactSection}>
                <div className={styles.contactGrid}>
                    <form className={styles.formCard} onSubmit={handleSubmit}>
                        <div className={styles.formHeader}>
                            <h2 className={styles.formTitle}>Send us a message</h2>
                            <p className={styles.formSubtitle}>Tell us what you need and we’ll respond quickly.</p>
                        </div>
                        {[
                            { id: 'firstName', label: 'First Name', type: 'text', value: firstName, setValue: setFirstName, error: errors.firstName, autoComplete: 'given-name' },
                            { id: 'lastName', label: 'Last Name', type: 'text', value: lastName, setValue: setLastName, error: errors.lastName, autoComplete: 'family-name' },
                            { id: 'email', label: 'Email', type: 'email', value: email, setValue: setEmail, error: errors.email, autoComplete: 'email' },
                            { id: 'message', label: 'Message', type: 'textarea', value: message, setValue: setMessage, error: errors.message, autoComplete: 'off' }
                        ].map(({ id, label, type, value, setValue, error, autoComplete }) => (
                            <div key={id} className={styles.fieldGroup}>
                                <label className={styles.label} htmlFor={id}>
                                    {label}
                                </label>
                                {type === 'textarea' ? (
                                    <textarea
                                        id={id}
                                        name={id}
                                        className={styles.textarea}
                                        value={value}
                                        onChange={(e) => setValue(e.target.value)}
                                        onFocus={() => setErrors(prevErrors => ({ ...prevErrors, [id]: '' }))}
                                        aria-invalid={Boolean(error)}
                                        aria-describedby={error ? `${id}-error` : undefined}
                                        required
                                    />
                                ) : (
                                    <input
                                        id={id}
                                        type={type}
                                        name={id}
                                        className={styles.field}
                                        value={value}
                                        onChange={(e) => setValue(e.target.value)}
                                        onFocus={() => setErrors(prevErrors => ({ ...prevErrors, [id]: '' }))}
                                        autoComplete={autoComplete}
                                        aria-invalid={Boolean(error)}
                                        aria-describedby={error ? `${id}-error` : undefined}
                                        required
                                    />
                                )}
                                {error && (
                                    <p className={styles.fieldError} id={`${id}-error`}>
                                        {error}
                                    </p>
                                )}
                            </div>
                        ))}
                        <div className={styles.captchaGroup}>
                            {isCaptchaReady ? (
                                <ReCAPTCHA
                                    sitekey={siteKey ?? ''}
                                    onChange={handleCaptchaChange}
                                />
                            ) : (
                                <p className={styles.fieldError}>
                                    CAPTCHA is unavailable. Please try again later.
                                </p>
                            )}
                            {errors.captcha && <p className={styles.fieldError}>{errors.captcha}</p>}
                        </div>
                        <button
                            type='submit'
                            className={styles.submitButton}
                            disabled={isSubmitting || !isCaptchaReady}
                        >
                            Submit
                        </button>
                    </form>
                    <aside className={styles.infoCard}>
                        <h3 className={styles.infoTitle}>Contact information</h3>
                        <p className={styles.infoSubtitle}>Prefer another channel? Reach us directly.</p>
                        {isLoading ? (
                            <p className={styles.infoText}>Loading contact information...</p>
                        ) : (
                            [
                                { Icon: AtSymbolIcon, label: 'Email', value: contactInfo.email },
                                { Icon: PhoneIcon, label: 'Phone', value: contactInfo.phone },
                                { Icon: MapIcon, label: 'Address', value: contactInfo.address },
                                { Icon: ClockIcon, label: 'Business Hours', value: contactInfo.hours }
                            ].map(({ Icon, label, value }) => (
                                <div key={label} className={styles.infoRow}>
                                    <span className={styles.infoIcon}>
                                        <Icon className={styles.infoIconSvg} />
                                    </span>
                                    <div>
                                        <p className={styles.infoLabel}>{label}</p>
                                        <p className={styles.infoValue}>{value}</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </aside>
                </div>
            </section>

            <section className={styles.subscribeSection}>
                <div className={styles.subscribeCard}>
                    <div>
                        <p className={styles.subscribeEyebrow}>Ready to watch?</p>
                        <h2 className={styles.subscribeTitle}>Subscribe now and never miss a fight night.</h2>
                        <p className={styles.subscribeCopy}>
                            Get instant access to exclusive interviews and highlights from every major card.
                        </p>
                    </div>
                    <div className={styles.subscribeActions}>
                        <Button
                            style={[styles.primaryButton, styles.ctaButton].join(' ')}
                            styleType={ButtonStyleType.FILL}
                            textSize={TextSize.MEDIUM}
                            text="Start membership"
                            callback={() => NavigateToInternalUrl(buildJoinUrl({ source: 'contact' }))}
                        />
                        <Button
                            style={[styles.secondaryButton, styles.ctaButton].join(' ')}
                            styleType={ButtonStyleType.OUTLINE}
                            textSize={TextSize.MEDIUM}
                            text="View plans"
                            callback={() => NavigateToInternalUrl(Routes.PRICING)}
                        />
                    </div>
                </div>
            </section>

            {shouldRenderPromoSection ? (
                <section className={styles.section}>
                    <SectionHeader
                        title="Featured promos"
                        subtitle="A quick snapshot of the best ways to join the action."
                    />
                    <div className={styles.promoGrid}>
                        {promos.map((promo) => <PromoCard key={promo.id} promo={promo} />)}
                    </div>
                </section>
            ) : null}

            <section className={styles.sectionAlt}>
                <SectionHeader
                    title="Highlights"
                    subtitle="Catch up on the biggest moments to share with your crew."
                />
                <div className={styles.highlightRail} role="list">
                    {highlightStatus === 'loading'
                        ? Array.from({ length: 3 }, (_, index) => (
                              <div key={`highlight-skeleton-${index}`} className={styles.highlightSkeleton} aria-busy="true">
                                  <div className={styles.highlightSkeletonMedia} />
                                  <div className={styles.highlightSkeletonBody}>
                                      <div className={styles.highlightSkeletonLine} />
                                      <div className={styles.highlightSkeletonLine} />
                                      <div className={styles.highlightSkeletonLine} />
                                  </div>
                              </div>
                          ))
                        : null}
                    {highlightStatus === 'error' ? (
                        <div className={styles.highlightErrorCard} role="status">
                            <p>{highlightError}</p>
                        </div>
                    ) : null}
                    {highlightStatus === 'success'
                        ? highlights.map((highlight) => (
                              <HighlightCard key={highlight.id} highlight={highlight} />
                          ))
                        : null}
                </div>
            </section>
        </main>
    );
};

export default ContactUsPage;
